import { useQuery, useMutation } from '@apollo/client';
import { TAREAS_HOUSEKEEPING, ACTUALIZAR_TAREA_HOUSEKEEPING } from '../../graphql/housekeeping';
import { Clock, Play, CheckCircle, BedDouble, Sparkles } from 'lucide-react';
import './LimpiezaDashboard.css';

const TIPO_LABEL = {
  limpieza: 'Limpieza',
  mantenimiento: 'Mantenimiento',
  inspeccion: 'Inspección',
  limpieza_profunda: 'Limpieza Profunda',
};

const PRIORIDAD_BADGE = {
  urgente: { label: 'Urgente', className: 'ld__badge--urgente' },
  alta: { label: 'Alta', className: 'ld__badge--alta' },
  normal: { label: 'Normal', className: 'ld__badge--normal' },
  baja: { label: 'Baja', className: 'ld__badge--baja' },
};

function LimpiezaDashboard({ usuario }) {
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

  const { data, loading } = useQuery(TAREAS_HOUSEKEEPING, {
    variables: { fecha: hoy, usuario_id: usuario?.id },
    pollInterval: 10000,
  });

  const [actualizarTarea] = useMutation(ACTUALIZAR_TAREA_HOUSEKEEPING, {
    refetchQueries: [{ query: TAREAS_HOUSEKEEPING, variables: { fecha: hoy, usuario_id: usuario?.id } }],
  });

  const tareas = data?.tareasHousekeeping || [];
  const pendientes = tareas.filter(t => t.estado === 'pendiente').length;
  const enProgreso = tareas.filter(t => t.estado === 'en_progreso').length;
  const completadas = tareas.filter(t => t.estado === 'completada').length;

  const handleActualizar = async (id, estado) => {
    try {
      await actualizarTarea({ variables: { id: parseInt(id), input: { estado } } });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="ld">
      <div className="ld__header">
        <div className="ld__header-icon">
          <Sparkles size={28} />
        </div>
        <div>
          <h1 className="ld__title">Mis Tareas de Hoy</h1>
          <p className="ld__subtitle">Bienvenido, {usuario?.nombre || 'Usuario'}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="ld__stats">
        <div className="ld__stat ld__stat--pendiente">
          <Clock size={20} />
          <div>
            <span className="ld__stat-num">{pendientes}</span>
            <span className="ld__stat-label">Pendientes</span>
          </div>
        </div>
        <div className="ld__stat ld__stat--progreso">
          <Play size={20} />
          <div>
            <span className="ld__stat-num">{enProgreso}</span>
            <span className="ld__stat-label">En Progreso</span>
          </div>
        </div>
        <div className="ld__stat ld__stat--completada">
          <CheckCircle size={20} />
          <div>
            <span className="ld__stat-num">{completadas}</span>
            <span className="ld__stat-label">Completadas</span>
          </div>
        </div>
      </div>

      {/* Task list */}
      {loading ? (
        <div className="ld__loading">Cargando tareas...</div>
      ) : tareas.length === 0 ? (
        <div className="ld__empty">
          <CheckCircle size={48} />
          <p>No tienes tareas asignadas para hoy</p>
        </div>
      ) : (
        <div className="ld__tareas">
          {tareas.filter(t => t.estado !== 'completada' && t.estado !== 'cancelada').map(tarea => (
            <div key={tarea.id} className={`ld__tarea ld__tarea--${tarea.prioridad}`}>
              <div className="ld__tarea-header">
                <div className="ld__tarea-room">
                  <BedDouble size={18} />
                  <span className="ld__tarea-num">Hab. {tarea.habitacion?.numero || 'N/A'}</span>
                  <span className="ld__tarea-tipo-hab">{tarea.habitacion?.tipo || ''}</span>
                </div>
                <span className={`ld__badge ${PRIORIDAD_BADGE[tarea.prioridad]?.className || ''}`}>
                  {PRIORIDAD_BADGE[tarea.prioridad]?.label || tarea.prioridad}
                </span>
              </div>
              <div className="ld__tarea-tipo">{TIPO_LABEL[tarea.tipo] || tarea.tipo}</div>
              {tarea.notas && <div className="ld__tarea-notas">{tarea.notas}</div>}
              <div className="ld__tarea-actions">
                {tarea.estado === 'pendiente' && (
                  <button className="ld__btn ld__btn--iniciar" onClick={() => handleActualizar(tarea.id, 'en_progreso')}>
                    <Play size={14} /> Iniciar
                  </button>
                )}
                {(tarea.estado === 'pendiente' || tarea.estado === 'en_progreso') && (
                  <button className="ld__btn ld__btn--completar" onClick={() => handleActualizar(tarea.id, 'completada')}>
                    <CheckCircle size={14} /> Completar
                  </button>
                )}
                {tarea.estado === 'en_progreso' && (
                  <span className="ld__estado-badge">En progreso...</span>
                )}
              </div>
            </div>
          ))}

          {/* Completed tasks at bottom */}
          {tareas.filter(t => t.estado === 'completada').length > 0 && (
            <>
              <h3 className="ld__section-title">Completadas hoy</h3>
              {tareas.filter(t => t.estado === 'completada').map(tarea => (
                <div key={tarea.id} className="ld__tarea ld__tarea--completada">
                  <div className="ld__tarea-header">
                    <div className="ld__tarea-room">
                      <CheckCircle size={18} className="ld__icon--done" />
                      <span className="ld__tarea-num">Hab. {tarea.habitacion?.numero || 'N/A'}</span>
                    </div>
                    <span className="ld__badge ld__badge--completada">Completada</span>
                  </div>
                  <div className="ld__tarea-tipo">{TIPO_LABEL[tarea.tipo] || tarea.tipo}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default LimpiezaDashboard;
